import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common';
import { getGraphQLClient, UPDATE_USER_LANGUAGE } from '../../lib/graphql';
import BackgroundJobsMenu from '../ai/BackgroundJobsMenu';

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateUserLanguage } = useAuth();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const languages = ['en', 'lt', 'ru'] as const;
  const languageFlags: Record<string, string> = { en: '🇬🇧', lt: '🇱🇹', ru: '🇷🇺' };
  const languageLabels: Record<string, string> = { en: 'English', lt: 'Lietuvių', ru: 'Русский' };
  const languageShortLabels: Record<string, string> = { en: 'EN', lt: 'LT', ru: 'RU' };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = async (newLang: string) => {
    i18n.changeLanguage(newLang);
    setLangDropdownOpen(false);

    if (user) {
      try {
        const client = getGraphQLClient();
        await client.request(UPDATE_USER_LANGUAGE, { language: newLang });
        updateUserLanguage(newLang);
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <motion.div
          whileHover={{ rotate: 10 }}
          className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center"
        >
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </motion.div>
        <span className="text-xl font-bold text-gradient hidden sm:block">{t('app.name')}</span>
      </Link>

      <div className="flex items-center gap-2 sm:gap-4">
        <BackgroundJobsMenu />
        <div className="relative" ref={langDropdownRef}>
          <button
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <span>{languageFlags[i18n.language] || languageFlags.en}</span>
            <span>{languageShortLabels[i18n.language] || languageShortLabels.en}</span>
            <svg
              className={`w-4 h-4 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {langDropdownOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                    i18n.language === lang
                      ? 'bg-gray-50 dark:bg-gray-700/50 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span>{languageFlags[lang]}</span>
                  <span>{languageLabels[lang]}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {user && (
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/profile"
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
              <span className="hidden sm:block">{user.name || t('profile.title')}</span>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              {t('nav.logout')}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
